import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, useMatch } from 'react-router-dom';

import { Button, Icon, TextInputHelper, Typography } from '@packages/ui/shared';
import { useAuthState } from '@packages/contexts/auth';
import { ANALYTICS_EVENTS } from '@packages/core/analytics';
import { getLogger } from '@packages/core/logging';
import { analyticsService } from '@web/services/singletons';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@web/core/hooks';
import { createUseStyles } from 'react-jss';
import colors from '@packages/core/styles/colors';
import { useVinSessionState } from '@web/vin-sessions/context/vin-sessions.hooks';
import type { StisFilterValues } from './stis-inline-filters';
import { StisSearchCriteria } from './stis-search-criteria';
import {
    hasTechTips,
    isAccessoryOnly,
    isFullVin,
    normalizeVin,
    toSearchFilters,
    type VinError,
} from './vehicle-filter.utils';
import { DocumentCodeMenu } from './document-code-menu';
import { DOCUMENT_CODE_MIN_QUERY, DOCUMENT_CODE_RESULT_LIMIT, normalizeDocumentCode } from './document-code.utils';
import { useDocumentCodes } from './hooks/use-document-codes';
import { loadListFilters, saveListFilters } from './publication-list-filters.storage';
import { DEFAULT_ADMIN_LIST_SORT, withDefaultListSort } from './publication-list-filter.util';
import StisTable from './stis-table';
import DocsPagination from '../docs/docs-pagination';
import { Col, Modal, Row } from 'react-bootstrap';
import { handleVinSessionError, publicationTypes } from '@web/vin-sessions/utils/vinSessionUtils';
import {
    PageDetail,
    PublicationPreviewContent,
    StisDocument,
    StisPublicationAction,
    VIN,
} from '@web/vin-sessions/types';
import { docsService, vinSessionService } from '@web/services/singletons';
import { SORT_DIRECTION } from '@web/components/table';
import { AdminHubHeader } from '@web/administration/components/admin-hub/admin-hub-header';
import { usePublicationsDispatch } from './context/publications.hooks';
import { cloneDeep } from 'lodash';
import { DocumentPreviewModal } from './document-preview-modal';
import { PdfHighlighter } from './pdf-highlighter';
import { handleLogin } from '@web/views/utils';
import { PopupGuidanceBanner, isPopupGuidanceDismissed, persistPopupGuidanceDismissed } from './popup-guidance-banner';
import { PopupGuidanceHelpPill } from './popup-guidance-help-pill';
import { publicationsActions } from './context/publications.state';
import { getSaveErrorMessage } from './save-error.utils';
import { getPublicationsEmptyState } from './empty-state.utils';
import { useGoogleAnalytics } from '@web/core/google-analytics/use-google-analytics';
import { GoogleAnalyticsEvents } from '@web/core/google-analytics';

const logger = getLogger('vin-sessions');

export type Publicationtype = {
    icon: string;
    publicationType: string;
    id?: string;
};

export type PublicationTypeResponse = {
    id: string;
    description: string;
};

type StisFilter = {
    title: string;
    filter: string;
    navigateLink: string;
    show: boolean;
};

const clearAllButtonStyle = { borderColor: colors.blueOne, borderWidth: 1 } as const;

const useStyles = createUseStyles({
    flexGap: {
        gap: 20,
    },
    tabsBarEnd: {
        marginLeft: 'auto',
        display: 'flex',
        alignItems: 'center',
    },
    cursorPointer: {
        cursor: 'pointer',
    },
    dropdown: {
        marginLeft: 10,
        '& .dropdown-menu': {
            width: 300,
            maxHeight: 300,
            padding: '8px 0 !important',
            borderRadius: `3px !important`,
            border: `1px solid ${colors.grayThree} !important`,
            boxShadow: `0px 16px 12px -12px rgba(0, 0, 0, 0.16) !important`,
            overflowY: 'auto !important',
        },
        '& .dropdown-menu::-webkit-scrollbar': {
            width: 10,
        },
        '& .dropdown-menu::-webkit-scrollbar-track': {
            backgroundColor: '#ffffff',
        },
        '& .dropdown-menu::-webkit-scrollbar-thumb': {
            backgroundColor: colors.blueFour,
            borderRadius: 10,
            border: '2px solid #ffffff',
        },
    },
    dropdownItem: {
        padding: '5px 10px',
        height: 40,
        display: 'flex',
        alignItems: 'center',
        '&:hover': {
            backgroundColor: colors.blueFour,
        },
        '& label': {
            display: 'flex',
            alignItems: 'center',
        },
    },
    publicationItem: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 5,
        marginLeft: 5,
        padding: '8px',
    },
    /** Keeps the filters clear of the pop-up help pill and the admin button beside them. */
    results: {
        scrollMarginTop: 12,
        '&:focus': {
            outline: 'none',
        },
    },
    searchRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 24,
    },
    searchRowAligned: {
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) minmax(300px, 440px)',
        alignItems: 'center',
        gap: 34,
        paddingRight: 21,
        '@media (max-width: 700px)': {
            gridTemplateColumns: 'minmax(0, 1fr)',
            gap: 12,
            paddingRight: 0,
        },
    },
    searchActions: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        alignItems: 'center',
        gap: 16,
        width: '100%',
    },
    searchButtonSlot: {
        minWidth: 0,
    },
    filterContainerAdmin: {
        flex: '1 1 auto',
        width: 'auto',
        minWidth: 0,
    },
    searchActionsCompact: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flex: '0 0 auto',
    },
    filterContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        flexWrap: 'wrap',
        width: '100%',
    },
    /**
     * Takes whatever the filters leave, so the row fills the width on a wide screen and the
     * search box is the first thing to shrink on a laptop. It used to be a flat 50%, which
     * both pushed the filters onto a second row and left a gap on a desktop.
     */
    searchInputWrapper: {
        position: 'relative',
        flex: '1 1 260px',
        minWidth: 200,
    },
    filterContainerNoWrap: {
        flexWrap: 'nowrap',
    },
    searchInputWrapperTight: {
        flex: '1 1 160px',
        minWidth: 120,
    },
    publicationsHover: {
        '&:hover': {
            backgroundColor: colors.blueFour,
        },
    },
    banner: {
        padding: 16,
        display: 'flex',
        borderRadius: 8,
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: colors.blueOne,
    },
});

export const StisPublications = () => {
    const selectedTabHeadStyles = {
        borderBottomColor: colors.blueOne,
        color: colors.blueOne,
        borderBottomWidth: 2,
        paddingBottom: 5,
    };
    const { t } = useTranslation();
    const navigate = useNavigate();
    const classes = useStyles();
    const { account, showUnauthorizedPrompt } = useAuthState();
    const { selectedVin } = useVinSessionState();
    const publicationDispatch = usePublicationsDispatch();
    const { pathname, search } = useLocation();
    const { googleAnalyticsService } = useGoogleAnalytics();
    const isManageAdminRoute = useMatch('/administration/*');
    const searchInputRef = useRef(null);
    const inputWrapperRef = useRef<HTMLDivElement>(null);
    /**
     * Set to the search term before any user-initiated fetch,
     * then consumed and cleared in `fetchPublications` to gate GA attribution.
     */
    const pendingSearchTermRef = useRef<string | null>(null);

    const queryParams = useQuery();
    const stisPublicationFilter =
        queryParams.getAll('filter').length === 1 ? queryParams.get('filter') || 'all' : 'all';
    const textSearchQuery = queryParams.get('query') || '';
    const sizeFromQuery = queryParams.get('size') || '10';
    const pageFromQuery = queryParams.get('page') || '0';
    const publicationMap = new Map(publicationTypes.map((p) => [p.publicationType, p.icon]));
    const [newTextSearch, setNewTextSearch] = useState(textSearchQuery);
    const parsedSize = sizeFromQuery ? parseInt(sizeFromQuery, 10) : 10;
    const parsedPage = pageFromQuery ? parseInt(pageFromQuery, 10) : 0;
    // The header shows the default sort too, so the arrow matches the order the rows came back in
    const defaultSort = isManageAdminRoute && !textSearchQuery ? DEFAULT_ADMIN_LIST_SORT.split(',') : null;
    const parsedSortOrder = queryParams.get('sort') ?? defaultSort?.[0] ?? null;
    const parsedSortDirection = queryParams.get('sortDir') ?? defaultSort?.[1].toUpperCase() ?? null;

    const [publicationsClone, setPublicationsClone] = useState<StisDocument[]>([]);
    const [publications, setPublications] = useState<StisDocument[]>([]);
    const [isAlertBarOpen, setisAlertBarOpen] = useState<boolean>(true);
    const [publicationType, setPublicationType] = useState<Publicationtype[]>([]);
    const [filterResponse, setFilterResponse] = useState<any>();
    const [modelYear, setModelYear] = useState([]);
    const [carline, setCarLine] = useState([]);
    const [vehicleModel, setVehicleModel] = useState([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [publicationPreviewContent, setPublicationPreviewContent] = useState<PublicationPreviewContent>();
    const [previewModal, setPreviewModal] = useState<boolean>(false);
    const [page, setPage] = useState<number>(0);
    const [highlightedWords, setHighlightedWords] = useState<string[]>();

    const [fetchDocsFlag, setFetchDocsFlag] = useState<boolean>(false);
    const [pageDetail, setPageDetail] = useState<PageDetail>({ size: 0, number: 0, totalElements: 0, totalPages: 0 });
    const [params, setParams] = useState<string>('');
    const [success, setSuccess] = useState<boolean>(false);
    const [additionalFilters, setAdditionalFilters] = useState<any>(() => loadListFilters(pathname));
    const [dropdownWidth, setDropdownWidth] = useState<number | undefined>();
    const isAdminList = Boolean(isManageAdminRoute);
    const isCriteriaOpenByDefault = useCallback((filter: string) => !isAdminList && filter === 'all', [isAdminList]);
    const [isCriteriaCollapsed, setIsCriteriaCollapsed] = useState<boolean>(
        () => !isCriteriaOpenByDefault(stisPublicationFilter)
    );
    const [draftFilters, setDraftFilters] = useState<Partial<StisFilterValues>>(() => loadListFilters(pathname));

    const isAccessoryOnlySelected = isAccessoryOnly(draftFilters.publicationTypes);
    const [partNumber, setPartNumber] = useState('');
    const codeScope = {
        publicationTypeIds: (draftFilters.publicationTypes ?? []).map((type) => type.id).filter(Boolean),
        carlineIds: (draftFilters.carline ?? []).map((carline) => carline.id).filter(Boolean),
        modelYear: draftFilters.modelYear ? Number(draftFilters.modelYear) : undefined,
    };
    const documentCodes = useDocumentCodes();
    const [isCodeMenuOpen, setIsCodeMenuOpen] = useState(false);
    const [codeBrowseLimit, setCodeBrowseLimit] = useState(DOCUMENT_CODE_RESULT_LIMIT);

    const codeMenuOptions = documentCodes.search(newTextSearch, codeScope, codeBrowseLimit);

    const partNumberLookup = {
        browse: (limit: number) => documentCodes.browse(codeScope, limit),
        search: (query: string) => documentCodes.search(query, codeScope),
        total: () => documentCodes.total(codeScope),
    };

    const wasAccessoryOnly = useRef(isAccessoryOnlySelected);
    useEffect(() => {
        if (wasAccessoryOnly.current === isAccessoryOnlySelected) {
            return;
        }
        wasAccessoryOnly.current = isAccessoryOnlySelected;
        setNewTextSearch('');
        setPartNumber('');
        setIsCodeMenuOpen(false);
    }, [isAccessoryOnlySelected]);
    const [vinError, setVinError] = useState<VinError | null>(null);
    const searchModeRef = useRef<boolean>(false);
    const resultsRef = useRef<HTMLDivElement>(null);
    const lastFetchKeyRef = useRef<string | null>(null);

    const [pdfPreview, setPdfPreview] = useState<{
        url: string;
        keyword: string;
        pageNumber: number;
    } | null>(null);

    const searchParams = new URLSearchParams(search);
    const filter = searchParams.get('filter') || 'all';

    useEffect(() => {
        saveListFilters(pathname, additionalFilters);
    }, [pathname, additionalFilters]);

    useEffect(() => {
        const vinValue = selectedVin?.vinRecord.vin;
        if (vinValue) return;
        analyticsService.logEvent(
            ANALYTICS_EVENTS.STIS_OPENED,
            {
                screen: 'stis_publications',
                source: 'stis',
                state: 'opened',
                vin: vinValue,
                vinPresent: !!vinValue,
            },
            undefined,
            'stis'
        );

        return () => {
            analyticsService.logEvent(
                ANALYTICS_EVENTS.STIS_OPENED,
                {
                    screen: 'stis_publications',
                    source: 'stis',
                    state: 'closed',
                    vin: vinValue,
                    vinPresent: !!vinValue,
                },
                undefined,
                'stop'
            );
        };
    }, []);

    useEffect(() => {
        if (parsedSortOrder && parsedSortDirection) {
            setParams(
                `filter=${
                    stisPublicationFilter === 'favorites'
                        ? 'favorite'
                        : stisPublicationFilter === 'recently-viewed'
                          ? 'recent'
                          : stisPublicationFilter
                }${textSearchQuery.length > 0 ? `&query=${textSearchQuery}` : ''}&page=${String(
                    0
                )}&size=${parsedSize}&sort=${parsedSortOrder},${parsedSortDirection.toLowerCase()}`
            );
        }
    }, [stisPublicationFilter]);

    // Without this the previous tab's rows and count stay on screen while the new tab loads.
    useEffect(() => {
        setPublications([]);
        setPageDetail({ size: 0, number: 0, totalElements: 0, totalPages: 0 });
    }, [stisPublicationFilter]);

    /**
     * Removing the last rows of a page leaves the user on a page that no longer exists, showing an
     * empty table. Steps back to the last page that still has rows.
     */
    const goToLastAvailablePage = useCallback(
        (page: PageDetail, contentLength: number) => {
            if (contentLength > 0 || page.number === 0) {
                return false;
            }
            const target = Math.max(0, Math.min(page.number - 1, page.totalPages - 1));
            const updatedParams = new URLSearchParams(search);
            updatedParams.set('page', String(target));
            navigate(`${pathname}?${updatedParams.toString()}`);
            return true;
        },
        [navigate, pathname, search]
    );

    useEffect(() => {
        if (inputWrapperRef && inputWrapperRef.current) {
            const width = inputWrapperRef.current.offsetWidth;
            setDropdownWidth(width);
        }
    }, [isCodeMenuOpen]);

    useEffect(() => {
        if (filterResponse) {
            const publicationType = filterResponse.publicationType.map((item: PublicationTypeResponse) => ({
                id: item.id,
                icon: publicationMap.get(item.description) || 'carCheck',
                publicationType: item.description,
            }));

            setPublicationType(publicationType);
            setModelYear(filterResponse.modelYear);
            setCarLine(filterResponse.carline);
            setVehicleModel(filterResponse.vehicleModel);
        }
    }, [filterResponse]);

    useEffect(() => {
        const publicationsClone = cloneDeep(publications);
        setPublicationsClone(publicationsClone);
    }, [publications]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (inputWrapperRef && inputWrapperRef.current && !inputWrapperRef.current.contains(event.target as Node)) {
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    });

    const searchByText = useCallback(
        (text: string, filter: string) => {
            const apiFilter = filter === 'favorites' ? 'favorite' : filter === 'recently-viewed' ? 'recent' : filter;
            const urlParams = new URLSearchParams(search);
            urlParams.delete('page');
            urlParams.delete('size');
            urlParams.delete('sort');
            urlParams.delete('sortDir');
            urlParams.set('filter', filter); // keep original value so tab highlight stays active

            if (text) {
                urlParams.set('query', text);
            } else {
                urlParams.delete('query');
                navigate(`${pathname}?filter=${filter}&page=${String(0)}&size=${String(10)}`);
                setParams(`filter=${apiFilter}&page=${String(0)}&size=${String(10)}`);
                return;
            }
            navigate(`${pathname}?${urlParams.toString()}&page=${String(0)}&size=${String(10)}`);
            // Build API params separately with the converted filter value the backend expects
            const apiParams = new URLSearchParams(urlParams.toString());
            apiParams.set('filter', apiFilter);
            setParams(`${apiParams.toString()}&page=${String(0)}&size=${String(10)}`);
        },
        [navigate, pathname, search]
    );

    const [guidanceDismissed, setGuidanceDismissed] = useState<boolean>(isPopupGuidanceDismissed);
    const [guidanceReopened, setGuidanceReopened] = useState(false);
    const [popupBlocked, setPopupBlocked] = useState(false);

    const isServiceDiagnosticsFilterActive = useMemo(() => {
        return Boolean(
            additionalFilters?.publicationTypes?.some(
                (item: { publicationType: string }) => item.publicationType === 'Service Diagnostics'
            )
        );
    }, [additionalFilters]);

    const showPopupGuidance =
        popupBlocked || guidanceReopened || (isServiceDiagnosticsFilterActive && !guidanceDismissed);

    const handlePopupGuidanceClose = useCallback(() => {
        persistPopupGuidanceDismissed();
        setGuidanceDismissed(true);
        setGuidanceReopened(false);
        setPopupBlocked(false);
    }, []);

    const handlePopupBlocked = useCallback(() => {
        setPopupBlocked(true);
    }, []);

    const hasQuickFilters = useMemo(() => {
        if (additionalFilters) {
            return (
                (additionalFilters.hasOwnProperty('keyword') && additionalFilters.keyword != '') ||
                (additionalFilters.hasOwnProperty('publicationTypes') &&
                    additionalFilters.publicationTypes.length > 0) ||
                (additionalFilters.hasOwnProperty('modelYear') && additionalFilters.modelYear != undefined) ||
                (additionalFilters.hasOwnProperty('modelCode') && additionalFilters.modelCode.length > 0) ||
                (additionalFilters.hasOwnProperty('carline') && additionalFilters.carline.length > 0) ||
                Boolean(additionalFilters.trim) ||
                Boolean(additionalFilters.vin) ||
                Boolean(additionalFilters.publishedYear) ||
                Boolean(additionalFilters.publishedMonth) ||
                (additionalFilters.hasOwnProperty('visibilityLevels') && additionalFilters.visibilityLevels.length > 0)
            );
        }
    }, [additionalFilters]);

    const handleSortChange = useCallback(
        (sortBy: string, sortDirection: SORT_DIRECTION) => {
            const params = new URLSearchParams(search);
            params.set('page', parsedPage.toString());
            params.set('size', parsedSize.toString());
            params.set('sort', sortBy);
            params.set('sortDir', sortDirection);
            params.set('filter', stisPublicationFilter);

            navigate(`${pathname}?${params.toString()}`);
            setParams(
                `filter=${
                    stisPublicationFilter === 'favorites'
                        ? 'favorite'
                        : stisPublicationFilter === 'recently-viewed'
                          ? 'recent'
                          : stisPublicationFilter
                }${
                    textSearchQuery.length > 0 ? `&query=${textSearchQuery}` : ''
                }&page=${parsedPage.toString()}&size=${parsedSize.toString()}&sort=${sortBy},${sortDirection.toLowerCase()}`
            );
        },
        [navigate, pathname, search, parsedPage, parsedSize]
    );

    /** The list `filter` tab spelled the way the API wants it. */
    const apiFilter = useMemo(
        () =>
            stisPublicationFilter === 'favorites'
                ? 'favorite'
                : stisPublicationFilter === 'recently-viewed'
                  ? 'recent'
                  : stisPublicationFilter,
        [stisPublicationFilter]
    );

    /**
     * Applies the search-row filters. Replaces the filter panel's Apply button, so it runs on
     * every change instead of on a press, and paging resets to the first page each time.
     */
    const clearKeywordFilter = useCallback(() => {
        setAdditionalFilters((previous: Record<string, unknown>) => {
            const next = { ...previous };
            delete next.keyword;
            return next;
        });
    }, []);

    const trackFiltersApplied = useCallback(
        (values: StisFilterValues) => {
            const appliedFilterNames: string[] = [];
            if (values.publicationTypes.length > 0) {
                appliedFilterNames.push('publication_type');
            }
            if (values.modelYear) {
                appliedFilterNames.push('model_year');
            }
            if (values.carline.length > 0) {
                appliedFilterNames.push('carline');
            }
            if (values.trim) {
                appliedFilterNames.push('trim');
            }
            if (values.vin) {
                appliedFilterNames.push('vin');
            }
            if (values.publishedYear) {
                appliedFilterNames.push('published_year');
            }
            if (values.publishedMonth) {
                appliedFilterNames.push('published_month');
            }
            if (appliedFilterNames.length === 0) {
                return;
            }

            googleAnalyticsService.track(GoogleAnalyticsEvents.DocumentFilterApply, {
                entry_point: 'search_row',
                filters_applied: appliedFilterNames.join(','),
                filter_count: appliedFilterNames.length,
                search_type: apiFilter,
            });

            const vinValue = selectedVin?.vinRecord.vin;
            analyticsService.logEvent(ANALYTICS_EVENTS.STIS_FILTER_APPLIED, {
                screen: 'stis_publications',
                source: 'stis_filters',
                params: {
                    publicationTypes: values.publicationTypes
                        .map((publication) => publication.publicationType)
                        .join(','),
                    year: values.modelYear ?? '',
                    carline: values.carline.map((option) => option.description).join(','),
                    trim: values.trim?.label ?? '',
                },
                vin: vinValue,
                vinPresent: Boolean(vinValue),
            });
        },
        [apiFilter, googleAnalyticsService, selectedVin]
    );

    /*
     * Only carry the keys that hold a value. Three callers decide between the plain
     * list endpoint and the search endpoint on `Object.keys(additionalFilters).length`,
     * so an empty array left behind here reads as an active filter for the rest of the
     * session and stops the sort field ever being remapped.
     */
    const withFilterValues = (previous: Record<string, unknown>, values: StisFilterValues) => {
        const next = { ...previous };
        if (values.publicationTypes.length > 0) {
            next.publicationTypes = values.publicationTypes;
        } else {
            delete next.publicationTypes;
        }
        if (values.carline.length > 0) {
            next.carline = values.carline;
        } else {
            delete next.carline;
        }
        if (values.modelYear) {
            next.modelYear = values.modelYear;
        } else {
            delete next.modelYear;
        }
        if (values.trim) {
            next.trim = values.trim;
        } else {
            delete next.trim;
        }
        if (values.vin) {
            next.vin = values.vin;
        } else {
            delete next.vin;
        }
        if (values.publishedYear) {
            next.publishedYear = values.publishedYear;
        } else {
            delete next.publishedYear;
        }
        if (values.publishedMonth) {
            next.publishedMonth = values.publishedMonth;
        } else {
            delete next.publishedMonth;
        }
        return next;
    };

    const runSearch = useCallback(
        (
            keyword: string,
            values: Partial<StisFilterValues>,
            options: { keepOpen?: boolean; scroll?: boolean; foldNow?: boolean } = {}
        ) => {
            const next: StisFilterValues = {
                publicationTypes: values.publicationTypes ?? [],
                carline: values.carline ?? [],
                modelYear: values.modelYear ? String(values.modelYear) : undefined,
                trim: values.trim,
                vin: values.vin ? normalizeVin(values.vin) : undefined,
                publishedYear: values.publishedYear,
                publishedMonth: values.publishedMonth,
            };

            if (next.vin && !hasTechTips(next.publicationTypes) && !isFullVin(next.vin)) {
                setVinError('format');
                setIsCriteriaCollapsed(false);
                return;
            }
            setVinError(null);
            setDraftFilters(next);

            const searched = toSearchFilters(next);
            trackFiltersApplied(searched);
            setAdditionalFilters((previous: Record<string, unknown>) => withFilterValues(previous, searched));

            const trimmedKeyword = keyword.trim();
            if (trimmedKeyword) {
                pendingSearchTermRef.current = keyword;
            }
            setNewTextSearch(keyword);
            searchModeRef.current = true;
            if (options.foldNow) {
                setIsCriteriaCollapsed(true);
            }
            searchByText(trimmedKeyword ? keyword : '', stisPublicationFilter);
            setFetchDocsFlag((previous) => !previous);
        },
        [searchByText, stisPublicationFilter, trackFiltersApplied]
    );

    const clearEverything = useCallback(() => {
        clearKeywordFilter();
        if (isCriteriaOpenByDefault(stisPublicationFilter)) {
            setIsCriteriaCollapsed(false);
        }
        runSearch('', {}, { keepOpen: true });
    }, [clearKeywordFilter, runSearch, stisPublicationFilter, isCriteriaOpenByDefault]);

    const hasFilterValues = (values: Partial<StisFilterValues>) =>
        (values.publicationTypes ?? []).length > 0 ||
        (values.carline ?? []).length > 0 ||
        Boolean(values.modelYear) ||
        Boolean(values.trim) ||
        Boolean(values.vin) ||
        Boolean(values.publishedYear) ||
        Boolean(values.publishedMonth);

    const hasAnythingToClear =
        newTextSearch.length > 0 ||
        textSearchQuery.length > 0 ||
        hasFilterValues(draftFilters) ||
        hasFilterValues(additionalFilters) ||
        Boolean(additionalFilters.keyword);

    const fetchBody = useCallback(
        (selectedVin: VIN | undefined, searchQuery: string, filters: any, searchType: string) => {
            let body: any = {};
            if (searchQuery.length > 0) body.query = searchQuery;
            if (filters && filters.publicationTypes && filters.publicationTypes.length > 0)
                body.publicationTypeIds = filters?.publicationTypes?.map((item: any) => item.id);
            if (!selectedVin && filters && filters.modelCode && filters.modelCode.length > 0) {
                body.modelCodeId = (
                    vehicleModel.filter((item: any) => item.code == filters?.modelCode[0])[0] as {
                        id: string;
                        code: string;
                    }
                ).id;
            }
            if (filters && filters.modelYear) body.modelYear = filters?.modelYear;
            if (filters && filters.carline && filters.carline.length > 0) body.carlineId = filters?.carline[0].id;
            if (!selectedVin && filters?.trim?.vehicleId) {
                body.vehicleId = filters.trim.vehicleId;
            }
            if (!selectedVin && filters?.vin) {
                body.vin = filters.vin;
            }
            if (filters?.publishedYear) {
                body.publishedYear = Number(filters.publishedYear);
            }
            if (filters?.publishedMonth) {
                body.publishedMonth = Number(filters.publishedMonth);
            }
            if (filters && filters.visibilityLevels && filters.visibilityLevels.length > 0)
                body.visibilityLevels = filters.visibilityLevels;
            if (selectedVin) {
                logger.debug(
                    `Building the publications search body for the selected VIN [${selectedVin.vinRecord.vin}]`,
                    {
                        vin: selectedVin.vinRecord.vin,
                        modelCode: selectedVin.vinRecord.modelNumber,
                    }
                );
                if (selectedVin.vinRecord.modelNumber && !hasTechTips(filters?.publicationTypes)) {
                    body.modelCode = selectedVin.vinRecord.modelNumber;
                }
            }
            if (account && searchType) {
                body.searchType =
                    searchType === 'favorites' ? 'favorite' : searchType !== 'recently-viewed' ? searchType : 'recent';
            }
            if (account && !isManageAdminRoute) body.docStatus = 'published';
            return body;
        },
        [carline, vehicleModel, account, isManageAdminRoute]
    );

    const fetchPublications = useCallback(
        async (vin: VIN | undefined, params: string, searchQuery: string, searchType: string, filters?: any) => {
            const isUserSearch = pendingSearchTermRef.current === searchQuery;
            pendingSearchTermRef.current = null;

            setIsLoading(true);

            const listParams = withDefaultListSort(params, {
                isAdminList: Boolean(isManageAdminRoute),
                searchQuery,
            });
            const queryParams = new URLSearchParams(listParams);
            const page = queryParams.get('page') || 0;
            const size = queryParams.get('size') || 10;
            const sort = queryParams.get('sort');
            let updatedSort = sort;
            if (
                (searchQuery || hasQuickFilters || !account || searchModeRef.current) &&
                (sort === 'createdAt,asc' || sort === 'createdAt,desc')
            ) {
                updatedSort = 'created_at' + sort.slice(9);
            } else if (
                (searchQuery || hasQuickFilters || !account || searchModeRef.current) &&
                (sort === 'updatedAt,asc' || sort === 'updatedAt,desc')
            ) {
                updatedSort = 'updated_at' + sort.slice(9);
            }

            // /search-docs defaults to title asc when no sort is sent, which would drop Recently
            // Viewed out of view order as soon as a keyword or filter is used.
            const sortQuery =
                sort != null
                    ? `&sort=${updatedSort}`
                    : searchType === 'recently-viewed'
                      ? '&sort=lastViewedAt,desc'
                      : '';

            let vinParams = listParams;
            if (vinParams.includes('createdAt,asc')) {
                vinParams = vinParams.replace('createdAt,asc', 'created_at,asc');
            }
            if (vinParams.includes('createdAt,desc')) {
                vinParams = vinParams.replace('createdAt,desc', 'created_at,desc');
            }
            if (vinParams.includes('updatedAt,asc')) {
                vinParams = vinParams.replace('updatedAt,asc', 'updated_at,asc');
            }
            if (vinParams.includes('updatedAt,desc')) {
                vinParams = vinParams.replace('updatedAt,desc', 'updated_at,desc');
            }
            try {
                const startTime = Date.now();
                const vinValue = selectedVin?.vinRecord.vin;
                analyticsService.logEvent(ANALYTICS_EVENTS.STIS_SEARCH_PERFORMED, {
                    searchQuery: searchQuery,
                    vin: vinValue,
                    vinPresent: !!vinValue,
                });
                const response =
                    searchQuery.length > 0 || Object.keys(filters).length > 0 || searchModeRef.current
                        ? await vinSessionService.getPublicationsByQuery({
                              uri: `?page=${page}&size=${size}` + sortQuery,
                              body: fetchBody(selectedVin, searchQuery, filters, searchType),
                              vin: selectedVin ? selectedVin.vinRecord.vin : '',
                              publicFlag: !account ? true : false,
                              ignoreCache: true,
                          })
                        : vin
                          ? await vinSessionService.getPublications({
                                vin: vin.vinRecord.vin,
                                body: {
                                    carlineId: vin?.carlineId,
                                    searchType:
                                        searchType === 'favorites'
                                            ? 'favorite'
                                            : searchType !== 'recently-viewed'
                                              ? searchType
                                              : 'recent',
                                    modelYear: vin?.vinRecord.modelYear,
                                    docStatus: 'published',
                                },
                                uri: vinParams,
                                ignoreCache: true,
                            })
                          : !account
                            ? await vinSessionService.getPublicPublicationsByQuery({
                                  uri: `?page=${page}&size=${size}` + sortQuery,
                                  body: fetchBody(selectedVin, searchQuery, filters, searchType),
                                  vin: selectedVin ? selectedVin.vinRecord.vin : '',
                                  ignoreCache: true,
                              })
                            : await vinSessionService.getAllPublications({ uri: listParams, ignoreCache: true });
                if (response.success && Object.keys(response.data).length > 0) {
                    if (goToLastAvailablePage(response.data.page, response.data.content.length)) {
                        return;
                    }
                    if (newTextSearch) setSuccess(true);
                    const vinValue = selectedVin?.vinRecord.vin;

                    analyticsService.logEvent(ANALYTICS_EVENTS.STIS_SEARCH_SUCCESS, {
                        searchQuery: searchQuery,
                        searchResultLength: response.data.page.totalElements,
                        vin: vinValue,
                        vinPresent: !!vinValue,
                        latency: Date.now() - startTime,
                    });

                    setPublications(response.data.content);
                    setPageDetail(response.data.page);

                    if (isUserSearch) {
                        googleAnalyticsService.track(GoogleAnalyticsEvents.Search, {
                            success: true,
                            search_term: searchQuery,
                            results_count: response.data.page.totalElements,
                            search_type: searchType,
                        });
                    }
                } else if (response.success && response.data.page.totalElements === 0) {
                    const vinValue = selectedVin?.vinRecord.vin;
                    analyticsService.logEvent(ANALYTICS_EVENTS.STIS_SEARCH_NO_RESULTS, {
                        searchQuery: searchQuery,
                        searchResultLength: 0,
                        vin: vinValue,
                        vinPresent: !!vinValue,
                    });
                } else {
                    const vinValue = selectedVin?.vinRecord.vin;

                    analyticsService.logEvent(ANALYTICS_EVENTS.STIS_SEARCH_FAILED, {
                        searchQuery: searchQuery,
                        errorMessage: 'Unknown error',
                        vin: vinValue,
                        vinPresent: !!vinValue,
                    });

                    if (isUserSearch) {
                        googleAnalyticsService.track(GoogleAnalyticsEvents.Search, {
                            success: false,
                            search_term: searchQuery,
                            search_type: searchType,
                            error_type: 'unknown_error',
                        });
                    }
                }
            } catch (error) {
                const vinValue = selectedVin?.vinRecord.vin;
                analyticsService.logEvent(ANALYTICS_EVENTS.STIS_SEARCH_FAILED, {
                    searchQuery: searchQuery,
                    errorMessage: error.message,
                    vin: vinValue,
                    vinPresent: !!vinValue,
                });
                setIsLoading(false);
                lastFetchKeyRef.current = null;
                handleVinSessionError('STIS publications filter error', error);
                if (isUserSearch) {
                    googleAnalyticsService.track(GoogleAnalyticsEvents.Search, {
                        success: false,
                        search_term: searchQuery,
                        search_type: searchType,
                        error_type: 'unknown_error',
                    });
                }
                return;
            }
            setIsLoading(false);
        },
        [selectedVin, additionalFilters, hasQuickFilters, isManageAdminRoute, goToLastAvailablePage]
    );

    const fetchPublicationsWithDocStatus = async (
        params: string,
        searchQuery: string,
        docStatus: string,
        filters: Record<string, unknown>
    ) => {
        setIsLoading(true);
        const queryParams = new URLSearchParams(
            withDefaultListSort(params, { isAdminList: Boolean(isManageAdminRoute), searchQuery })
        );
        const page = queryParams.get('page') || 0;
        const size = queryParams.get('size') || 10;
        const sort = queryParams.get('sort');
        let updatedSort = sort;
        if ((searchQuery || hasQuickFilters) && (sort === 'createdAt,asc' || sort === 'createdAt,desc')) {
            updatedSort = 'created_at' + sort.slice(9);
        } else if ((searchQuery || hasQuickFilters) && (sort === 'updatedAt,asc' || sort === 'updatedAt,desc')) {
            updatedSort = 'updated_at' + sort.slice(9);
        }
        try {
            const response = await docsService.getPublicationsByDocStatus({
                uri: `?page=${page}&size=${size}` + (sort != null ? `&sort=${updatedSort}` : ''),
                // The status tabs have never been scoped to the open VIN, so no VIN is passed
                body: {
                    ...fetchBody(undefined, searchQuery, filters, ''),
                    docStatus: docStatus,
                },
            });
            if (response.success && Object.keys(response.data).length > 0) {
                if (newTextSearch) setSuccess(true);
                setPublications(response.data.content);
                setPageDetail(response.data.page);
            }
        } catch (error) {
            setIsLoading(false);
            handleVinSessionError('STIS publications filter error', error);
            return;
        }
        setIsLoading(false);
    };

    const getFilters = async () => {
        try {
            const response = account
                ? await vinSessionService.getFilter({
                      ignoreCache: true,
                  })
                : await vinSessionService.getPublicFilter({
                      ignoreCache: true,
                  });

            if (response.success && Object.keys(response.data).length > 0) {
                setFilterResponse(response.data);
            }
        } catch (error) {
            handleVinSessionError('Publications Fetch Error', error);
        }
    };

    const handleFavoriteClick = async (docId: string, isFavorite: boolean) => {
        try {
            const response = await vinSessionService.postFavoriteDocument({
                queryParameters: { docId: docId, isFavorite },
            });
            if (response.success) {
                if (stisPublicationFilter === 'favorites') {
                    setFetchDocsFlag((prev) => !prev);
                    return;
                }
                setPublicationsClone(
                    publicationsClone.map((publication) =>
                        publication.documentId === docId ? { ...publication, isFavorite } : publication
                    )
                );
            }
        } catch (error) {
            handleVinSessionError('Favorite Document Error', error);
            return;
        }
    };

    const handleRemoveRecentClick = async (docIds: string[]) => {
        if (docIds.length === 0) {
            return;
        }
        try {
            const response = await docsService.removeDocumentsFromRecents({ docIds });
            if (!response.success) {
                throw response.data;
            }
            setFetchDocsFlag((prev) => !prev);
        } catch (error) {
            logger.error(`Failed to remove the documents [${docIds.join(', ')}] from recently viewed`, {
                error,
                docIds,
            });
            publicationDispatch(
                publicationsActions.setToastMessage({
                    toast: {
                        toastMessage: t(
                            'stisPublications:recentlyViewed.removeError',
                            'Unable to remove the document from Recently Viewed'
                        ),
                        iconColor: 'white',
                        iconName: 'x-close',
                    },
                })
            );
        }
    };

    const handlePreviewButtonClick = async (
        docId: string,
        title: string,
        page: number,
        docType?: string,
        _fileName?: string,
        createdDate?: string,
        lastUpdated?: string
    ) => {
        try {
            if (newTextSearch == '') {
                return;
            }
            setPage(page);
            const response = await docsService.getDocumentPreview({
                queryParameters: {
                    query: newTextSearch,
                    docId: docId,
                    page: page,
                    size: 10,
                },
                account,
                ignoreCache: true,
            });
            if (response.success && Object.keys(response.data).length > 0) {
                if (page === 0) {
                    const vinValue = selectedVin?.vinRecord.vin;
                    analyticsService.logEvent(ANALYTICS_EVENTS.STIS_DOCUMENT_PREVIEWED, {
                        screen: 'stis_publications',
                        vin: vinValue,
                        vinPresent: !!vinValue,
                        source: 'stis_preview',
                        documentId: docId,
                        type: docType,
                        docType: docType,
                        createdDate,
                        lastUpdated,
                        searchQuery: newTextSearch,
                    });

                    googleAnalyticsService.track(GoogleAnalyticsEvents.DocumentPreview, {
                        document_id: docId,
                        entry_point: 'results_table',
                        document_type: docType,
                    });
                }

                setPublicationPreviewContent((prevContent) => ({
                    documentId: docId,
                    docType: docType,
                    createdDate,
                    lastUpdated,
                    title: title,
                    query: newTextSearch,
                    totalElements: response.data.page.totalElements,
                    totalPages: response.data.page.totalPages,
                    content:
                        page > 0 ? [...(prevContent?.content || []), ...response.data.content] : response.data.content,
                }));
                setPreviewModal(true);
            }
        } catch (error) {
            handleVinSessionError('Preview Fetch Error', error);
            return;
        }
    };

    const getPublicationVersions = async (docId: string) => {
        try {
            const response = await docsService.getPublicationVersions(docId);
            return response.data || [];
        } catch (error) {
            handleVinSessionError('Fetching Versions Error', error);
            return [];
        }
    };

    const handleScroll = (
        event: React.UIEvent<HTMLDivElement>,
        id: string,
        title: string,
        totalPages: number,
        docType?: string
    ) => {
        const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
        if (scrollTop + clientHeight + 1 >= scrollHeight) {
            setPage((prev) => {
                const newPage = prev + 1;
                if (newPage >= totalPages) {
                    return prev;
                }
                handlePreviewButtonClick(id, title, newPage, docType);
                return newPage;
            });
        }
    };

    const getHighlightedWords = (previewContent: PublicationPreviewContent) => {
        const boldWordsSet = new Set();
        previewContent.content.forEach((item) => {
            const matches = item.headline.match(/<b>(.*?)<\/b>/gi);
            if (matches) {
                matches.forEach((match) => {
                    const word = match.replace(/<\/?b>/gi, '').trim();
                    boldWordsSet.add(word);
                });
            }
        });
        return Array.from(boldWordsSet);
    };

    const fetchSelectedTabPublications = useCallback(
        async (searchType: 'favorites' | 'recent', params: string) => {
            const queryParams = new URLSearchParams(params);
            const page = queryParams.get('page') || 0;
            const size = queryParams.get('size') || 10;
            const sort = queryParams.get('sort');
            let updatedSort = sort;
            if (sort === 'createdAt,asc' || sort === 'createdAt,desc') {
                updatedSort = 'created_at' + sort.slice(9);
            } else if (sort === 'updatedAt,asc' || sort === 'updatedAt,desc') {
                updatedSort = 'updated_at' + sort.slice(9);
            }
            setIsLoading(true);
            try {
                const response = await docsService.getAccountDocs({
                    queryParameters: {
                        type: searchType === 'favorites' ? 'FAVORITE' : 'RECENT',
                        carlineId: selectedVin?.carlineId,
                        modelYear: selectedVin?.vinRecord.modelYear,
                        page,
                        size,
                        sort: updatedSort ?? undefined,
                    },
                });
                if (response.success) {
                    if (goToLastAvailablePage(response.data.page, response.data.content.length)) {
                        return;
                    }
                    setPublications(response.data.content);
                    if (setPageDetail) {
                        setPageDetail(response.data.page);
                    }
                }
            } catch (error) {
                logger.error(`Failed to fetch the publications for the selected tab [${searchType}]`, {
                    error,
                    searchType,
                });
            }
            setIsLoading(false);
        },
        [
            params,
            selectedVin,
            additionalFilters,
            hasQuickFilters,
            setPublications,
            setPageDetail,
            setIsLoading,
            goToLastAvailablePage,
        ]
    );

    useEffect(() => {
        if (stisPublicationFilter !== 'recently-viewed' || !account) {
            return;
        }
        const refreshOnReturn = () => {
            if (document.visibilityState === 'visible') {
                setFetchDocsFlag((previous) => !previous);
            }
        };
        document.addEventListener('visibilitychange', refreshOnReturn);
        return () => document.removeEventListener('visibilitychange', refreshOnReturn);
    }, [stisPublicationFilter, account]);

    useEffect(() => {
        const sortedParams = [...new URLSearchParams(params).entries()].sort().flat().join('=');
        const fetchKey = JSON.stringify([
            sortedParams,
            additionalFilters,
            newTextSearch,
            filter,
            fetchDocsFlag,
            selectedVin?.vinRecord.vin,
        ]);
        if (lastFetchKeyRef.current === fetchKey) {
            return;
        }
        lastFetchKeyRef.current = fetchKey;

        switch (filter) {
            case 'favorites':
                if (account) {
                    newTextSearch.length > 0 || Object.keys(additionalFilters).length > 0 || searchModeRef.current
                        ? fetchPublications(
                              selectedVin,
                              params,
                              newTextSearch,
                              stisPublicationFilter,
                              additionalFilters
                          )
                        : fetchSelectedTabPublications('favorites', params);
                }
                break;
            case 'recently-viewed':
                if (account) {
                    newTextSearch.length > 0 || Object.keys(additionalFilters).length > 0 || searchModeRef.current
                        ? fetchPublications(
                              selectedVin,
                              params,
                              newTextSearch,
                              stisPublicationFilter,
                              additionalFilters
                          )
                        : fetchSelectedTabPublications('recent', params);
                } else {
                    setPublications([]);
                }
                break;
            case 'draft':
                fetchPublicationsWithDocStatus(params, newTextSearch, 'draft', additionalFilters);
                break;
            case 'in-review':
                fetchPublicationsWithDocStatus(params, newTextSearch, 'IN_REVIEW', additionalFilters);
                break;
            case 'published':
                fetchPublicationsWithDocStatus(params, newTextSearch, 'published', additionalFilters);
                break;
            case 'archived':
                fetchPublicationsWithDocStatus(params, newTextSearch, 'archived', additionalFilters);
                break;
            default:
                if (params.length > 0)
                    fetchPublications(selectedVin, params, newTextSearch, stisPublicationFilter, additionalFilters);
                break;
        }
    }, [params, additionalFilters, fetchDocsFlag, stisPublicationFilter, selectedVin]);

    useEffect(() => {
        getFilters();
    }, []);

    const breadcrumbs = useMemo(() => {
        return [
            { title: t('stisPublications:breadcrumbs.adminHub', 'Administration Hub'), to: '/administration' },
            { title: t('stisPublications:breadcrumbs.stisPublications', 'STIS Publications') },
        ];
    }, [t]);

    useEffect(() => {
        if (publicationPreviewContent && publicationPreviewContent?.content.length > 0) {
            const highlightedWords = getHighlightedWords(publicationPreviewContent);
            setHighlightedWords(highlightedWords as string[]);
        }
    }, [publicationPreviewContent]);

    const rawFilters: StisFilter[] = useMemo(
        () => [
            {
                title: t('stisPublications:filters.all', 'All'),
                filter: 'all',
                navigateLink: isManageAdminRoute
                    ? `/administration/publications`
                    : !account || !selectedVin
                      ? `/stis${
                            newTextSearch.length > 0 && filter == 'all'
                                ? `?query=${encodeURIComponent(newTextSearch)}`
                                : ''
                        }`
                      : `/vin/${selectedVin?.vinRecord.vin}/stis${
                            newTextSearch.length > 0 && filter == 'all'
                                ? `?query=${encodeURIComponent(newTextSearch)}`
                                : ''
                        }`,
                show: account !== null,
            },
            {
                title: t('stisPublications:filters.recentlyViewed', 'Recently Viewed'),
                filter: 'recently-viewed',
                navigateLink:
                    !account || !selectedVin
                        ? `/stis?filter=recently-viewed${
                              newTextSearch.length > 0 && filter == 'recently-viewed'
                                  ? `&query=${encodeURIComponent(newTextSearch)}`
                                  : ''
                          }`
                        : `/vin/${selectedVin?.vinRecord.vin}/stis?filter=recently-viewed${
                              newTextSearch.length > 0 && filter == 'recently-viewed'
                                  ? `&query=${encodeURIComponent(newTextSearch)}`
                                  : ''
                          }`,
                show: account !== null && isManageAdminRoute === null,
            },
            {
                title: t('stisPublications:filters.favorites', 'Favorites'),
                filter: 'favorites',
                navigateLink:
                    !account || !selectedVin
                        ? `/stis?filter=favorites${
                              newTextSearch.length > 0 && filter == 'favorites'
                                  ? `&query=${encodeURIComponent(newTextSearch)}`
                                  : ''
                          }`
                        : `/vin/${selectedVin?.vinRecord.vin}/stis?filter=favorites${
                              newTextSearch.length > 0 && filter == 'favorites'
                                  ? `&query=${encodeURIComponent(newTextSearch)}`
                                  : ''
                          }`,
                show: account !== null && isManageAdminRoute === null,
            },
            {
                title: t('stisPublications:filters.draft', 'Draft'),
                filter: 'draft',
                navigateLink: `?filter=draft${
                    newTextSearch.length > 0 && filter == 'draft' ? `&query=${encodeURIComponent(newTextSearch)}` : ''
                }`,
                show: account !== null && isManageAdminRoute !== null,
            },
            {
                title: t('stisPublications:filters.published', 'Published'),
                filter: 'published',
                navigateLink: `?filter=published${
                    newTextSearch.length > 0 && filter == 'published'
                        ? `&query=${encodeURIComponent(newTextSearch)}`
                        : ''
                }`,
                show: account !== null && isManageAdminRoute !== null,
            },
            {
                title: t('stisPublications:filters.archived', 'Archived'),
                filter: 'archived',
                navigateLink: `?filter=archived${
                    newTextSearch.length > 0 && filter == 'archived'
                        ? `&query=${encodeURIComponent(newTextSearch)}`
                        : ''
                }`,
                show: account !== null && isManageAdminRoute !== null,
            },
        ],
        [t, account, selectedVin, isManageAdminRoute, newTextSearch]
    );

    const filters: StisFilter[] = useMemo(() => rawFilters.filter((filter) => filter.show), [rawFilters]);

    return (
        <div style={{ position: 'relative' }}>
            {isManageAdminRoute && <AdminHubHeader breadcrumbs={breadcrumbs} />}

            {pdfPreview && (
                <Modal show onHide={() => setPdfPreview(null)} size="xl" backdrop="static" centered>
                    <Modal.Header closeButton>
                        <Modal.Title>Document Preview</Modal.Title>
                    </Modal.Header>
                    <Modal.Body style={{ maxHeight: '80vh', overflowY: 'auto', padding: '0' }}>
                        <PdfHighlighter
                            documentUrl={pdfPreview.url}
                            keyword={pdfPreview.keyword}
                            pageNumber={pdfPreview.pageNumber}
                            highlightedWords={highlightedWords}
                        />
                    </Modal.Body>
                </Modal>
            )}

            <DocumentPreviewModal
                show={previewModal}
                title={t('stisPublications:preview.title', 'Keyword Previews')}
                onHideModal={() => setPreviewModal(false)}
                cancelButton={t('stisPublications:preview.buttons.cancel', 'Cancel')}
                confirmButton={t('stisPublications:preview.buttons.confirm', 'Open full document')}
                content={publicationPreviewContent}
                closeButton={true}
                handleScroll={handleScroll}
                setPdfPreview={setPdfPreview}
                highlightedWords={highlightedWords}
            />

            <div className="pt-3 pl-5 pr-7 d-flex">
                <div className="d-flex w-100 flex-column">
                    {!isManageAdminRoute && (
                        <div className="mb-4">
                            <Typography variant="h2">{t('stisPublications:title', 'STIS Publications')}</Typography>
                        </div>
                    )}

                    {isAlertBarOpen && !account && (
                        <Row className="mb-3">
                            <Col>
                                <div className={classes.banner}>
                                    <div style={{ display: 'flex', justifyContent: 'center', gap: 16 }}>
                                        <Icon name="info" color="white" size={20} />
                                        <Typography variant="labelRegular" color="white" style={{ marginTop: 1 }}>
                                            Some documents are only available within Subaru Techshare. To access them,
                                            please{' '}
                                            <a
                                                href="#"
                                                style={{
                                                    color: '#fff',
                                                    textDecoration: 'underline',
                                                    cursor: 'pointer',
                                                    fontWeight: 500,
                                                    borderBottom: '1px dotted #fff',
                                                }}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    handleLogin(showUnauthorizedPrompt, navigate);
                                                }}
                                            >
                                                Sign in
                                            </a>{' '}
                                            to the Techshare application.
                                            {/* or{' '}
                                            <a
                                                href="/create-account"
                                                style={{ color: '#fff', textDecoration: 'underline' }}
                                            >
                                                create an account.
                                            </a> */}
                                        </Typography>
                                    </div>
                                    <Icon name="x-close" color="white" onPress={() => setisAlertBarOpen(false)} />
                                </div>
                            </Col>
                        </Row>
                    )}

                    {(filters.length > 0 || (!showPopupGuidance && !isManageAdminRoute)) && (
                        <div className={`d-flex flex-row align-items-center ${classes.flexGap} mb-4 flex-wrap`}>
                            {filters.map((filter: StisFilter) => (
                                <div
                                    key={filter.filter}
                                    className={`${classes.cursorPointer} d-flex`}
                                    onClick={() => {
                                        if (stisPublicationFilter === filter.filter) return;
                                        navigate(filter.navigateLink);
                                        setParams(
                                            `filter=${
                                                filter.filter === 'favorites'
                                                    ? 'favorite'
                                                    : filter.filter === 'recently-viewed'
                                                      ? 'recent'
                                                      : filter.filter
                                            }&page=0&size=10`
                                        );
                                        setNewTextSearch('');
                                        setAdditionalFilters({});
                                        setDraftFilters({});
                                        setVinError(null);
                                        searchModeRef.current = false;
                                        setIsCriteriaCollapsed(!isCriteriaOpenByDefault(filter.filter));
                                    }}
                                >
                                    <Typography
                                        style={stisPublicationFilter === filter.filter ? selectedTabHeadStyles : {}}
                                        color="grayFive"
                                        variant="h6"
                                    >
                                        {filter.title}
                                    </Typography>
                                </div>
                            ))}
                            {!showPopupGuidance && !isManageAdminRoute && (
                                <div className={classes.tabsBarEnd}>
                                    <PopupGuidanceHelpPill onPress={() => setGuidanceReopened(true)} />
                                </div>
                            )}
                        </div>
                    )}

                    {showPopupGuidance && (
                        <PopupGuidanceBanner
                            show={showPopupGuidance}
                            blocked={popupBlocked}
                            onClose={handlePopupGuidanceClose}
                            style={{ marginBottom: 16 }}
                        />
                    )}

                    <StisSearchCriteria
                        publicationTypes={publicationType}
                        value={draftFilters}
                        appliedFilters={additionalFilters}
                        showVehicleFilters={!selectedVin}
                        sessionVin={selectedVin?.vinRecord.vin}
                        isSignedIn={Boolean(account)}
                        fallbackModelYears={modelYear}
                        fallbackCarlines={carline}
                        collapsed={isCriteriaCollapsed}
                        resultCount={isLoading ? undefined : pageDetail.totalElements}
                        vinError={vinError}
                        showAccessoryPartNumber={isAccessoryOnlySelected}
                        partNumber={partNumber}
                        partNumberLookup={partNumberLookup}
                        isPartNumberLoading={documentCodes.isLoading}
                        onPartNumberChange={(value) => {
                            setPartNumber(value);
                            setNewTextSearch(value);
                        }}
                        onCollapsedChange={setIsCriteriaCollapsed}
                        onChange={(values) => {
                            setDraftFilters(values);
                            setVinError(null);
                        }}
                        onApply={(values) => runSearch(newTextSearch, values)}
                        onCollapse={() => {
                            if (hasFilterValues(draftFilters) || Object.keys(additionalFilters).length > 0) {
                                runSearch(newTextSearch, draftFilters, { scroll: false, foldNow: true });
                                return;
                            }
                            setIsCriteriaCollapsed(true);
                        }}
                        onSubmit={() => runSearch(newTextSearch, draftFilters)}
                    />

                    <div className={isManageAdminRoute ? classes.searchRow : classes.searchRowAligned}>
                        <div
                            className={`${classes.filterContainer} ${
                                isManageAdminRoute ? classes.filterContainerAdmin : classes.filterContainerNoWrap
                            }`}
                        >
                            <div
                                ref={inputWrapperRef}
                                className={`${classes.searchInputWrapper} ${
                                    isManageAdminRoute ? '' : classes.searchInputWrapperTight
                                }`}
                                onBlur={(event) => {
                                    if (!event.currentTarget.contains(event.relatedTarget as Node)) {
                                        setIsCodeMenuOpen(false);
                                    }
                                }}
                            >
                                <TextInputHelper
                                    ref={searchInputRef}
                                    rightIcon={
                                        <Icon
                                            name={textSearchQuery || newTextSearch ? 'x-close' : 'magnifying-glass'}
                                            onPress={
                                                textSearchQuery || newTextSearch
                                                    ? () => {
                                                          searchByText('', stisPublicationFilter);
                                                          setNewTextSearch('');
                                                          setPartNumber('');
                                                          if (success) setFetchDocsFlag(!fetchDocsFlag);
                                                          setSuccess(false);
                                                          clearKeywordFilter();
                                                      }
                                                    : undefined
                                            }
                                        />
                                    }
                                    value={partNumber ? '' : newTextSearch}
                                    onChangeText={(text) => {
                                        setCodeBrowseLimit(DOCUMENT_CODE_RESULT_LIMIT);
                                        setIsCodeMenuOpen(true);
                                        setPartNumber('');
                                        if (textSearchQuery.length > 0 && text.length == 0) {
                                            searchByText('', stisPublicationFilter);
                                            setNewTextSearch('');
                                            clearKeywordFilter();
                                        } else {
                                            setNewTextSearch(text);
                                        }
                                    }}
                                    placeholder={'Search Keywords / Doc Codes / DTC'}
                                    onKeyPress={(e) => {
                                        if (e.nativeEvent.key === 'Enter') {
                                            if (!isManageAdminRoute) {
                                                runSearch(newTextSearch, draftFilters);
                                                return;
                                            }
                                            if (newTextSearch.trim()) {
                                                pendingSearchTermRef.current = newTextSearch;
                                                setIsCriteriaCollapsed(true);
                                            }
                                            searchByText(newTextSearch, stisPublicationFilter);
                                        }
                                        setIsCodeMenuOpen(false);
                                    }}
                                />
                                {isCodeMenuOpen && codeMenuOptions.length > 0 && (
                                    <DocumentCodeMenu
                                        options={codeMenuOptions}
                                        query={newTextSearch}
                                        isLoading={documentCodes.isLoading}
                                        width={dropdownWidth}
                                        hasMore={codeMenuOptions.length < documentCodes.total(codeScope, newTextSearch)}
                                        onReachEnd={() =>
                                            setCodeBrowseLimit((limit) => limit + DOCUMENT_CODE_RESULT_LIMIT)
                                        }
                                        moreLabel={t('publications:searchCriteria.scrollForMore', 'Scroll for more')}
                                        loadingLabel={t('publications:searchCriteria.loadingCodes', 'Loading codes')}
                                        emptyLabel={t('publications:searchCriteria.noCodeMatches', 'No matches found')}
                                        headerLabel={t(
                                            'publications:searchCriteria.matchedDocCodes',
                                            'Matched doc codes'
                                        )}
                                        onSelect={(code) => {
                                            setNewTextSearch(code);
                                            setPartNumber('');
                                            setIsCodeMenuOpen(false);
                                            runSearch(code, draftFilters);
                                        }}
                                    />
                                )}
                            </div>

                            {/* hidden temporarily for the time being */}
                            {/* <Dropdown className={classes.dropdown} show={isDropdownOpen} ref={dropdownRef}>
                                    <Dropdown.Toggle
                                    as={CustomToggle}
                                    id="action-dropdown"
                                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                    setIsDropdownOpen={setIsDropdownOpen}
                                    setIsDropdownTextOpen={setIsDropdownTextOpen}
                                    style
                                                backgroundColor:
                                                selectedPublications.length == 0 ? colors.white : colors.blueFour,
                                            }}
                                            >
                                            <Typography
                                                variant="label"
                                                style={{
                                                color: colors.blueOne,
                                                fontWeight: '800',
                                                textTransform: 'capitalize',
                                                }}
                                            >
                                                Publication Type
                                            </Typography>
                                            <Icon
                                                name="arrow-small-down"
                                                style={{ color: colors.blueOne, marginTop: 1 }}
                                                size={22}
                                            />
                                            </Dropdown.Toggle>
                                            <Dropdown.Menu>
                                            {publicationType.map((filter, index) => (
                                                <div key={index} className={classes.dropdownItem}>
                                                <label>
                                                    <input
                                                    type="checkbox"
                                                    value={filter.publicationType}
                                                    checked={selectedPublications.includes(filter)}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                        setSelectedPublications([...selectedPublications, filter]);
                                                        } else {
                                                        setSelectedPublications(
                                                            selectedPublications.filter(
                                                            (item: { publicationType: string }) =>
                                                                item.publicationType !== filter.publicationType
                                                            )
                                                        );
                                                        }
                                                    }}
                                                    />
                                                    <div className={classes.publicationItem}>
                                                    <PublicationType
                                                        publicationType={filter.publicationType}
                                                        size={18}
                                                    />
                                                    {filter.publicationType}
                                                    </div>
                                                </label>
                                                </div>
                                            ))}
                                            </Dropdown.Menu>
                                        </Dropdown> */}
                        </div>

                        <div className={isManageAdminRoute ? classes.searchActionsCompact : classes.searchActions}>
                            <div className={classes.searchButtonSlot}>
                                <Button
                                    block={!isManageAdminRoute}
                                    onPress={() => runSearch(newTextSearch, draftFilters)}
                                >
                                    {t('publications:searchCriteria.search', 'Search')}
                                </Button>
                            </div>
                            <Button
                                block={!isManageAdminRoute}
                                variant="ghost-blue"
                                style={isManageAdminRoute ? undefined : clearAllButtonStyle}
                                iconRight={
                                    <Icon
                                        name="x-close"
                                        size={16}
                                        color={hasAnythingToClear ? 'blueOne' : 'grayFive'}
                                    />
                                }
                                title={t('publications:searchCriteria.clearAll', 'Clear all')}
                                disabled={!hasAnythingToClear}
                                onPress={clearEverything}
                            />
                        </div>

                        {isManageAdminRoute && (
                            <div className="d-flex">
                                <Button
                                    iconRight={
                                        <Icon
                                            size={16}
                                            raised
                                            name="plus"
                                            raisedContainerStyle={{ width: 22, height: 22 }}
                                        />
                                    }
                                    numberOfLines={1}
                                    onPress={async () => {
                                        const createPublication = async () => {
                                            const response = await docsService.createPublication({});
                                            if (!response.success) {
                                                if (!response.aborted) {
                                                    publicationDispatch(
                                                        publicationsActions.setToastMessage({
                                                            toast: {
                                                                toastMessage: getSaveErrorMessage(
                                                                    response.data,
                                                                    'The publication was not created'
                                                                ),
                                                                iconColor: 'white',
                                                                iconName: 'x-close',
                                                            },
                                                        })
                                                    );
                                                }
                                                return;
                                            }
                                            if (typeof response.data === 'string') {
                                                try {
                                                    const parsedData = JSON.parse(response.data);
                                                    if (parsedData && parsedData.documentId) {
                                                        navigate(
                                                            `/administration/publication/${parsedData.documentId}?action=${StisPublicationAction.New}`
                                                        );
                                                    }
                                                } catch (error) {
                                                    logger.error(
                                                        'Failed to parse the create publication response data',
                                                        {
                                                            error,
                                                        }
                                                    );
                                                }
                                            }
                                        };
                                        await createPublication();
                                    }}
                                >
                                    Add a new publication
                                </Button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <div ref={resultsRef} tabIndex={-1} className={classes.results}>
                <StisTable
                    isLoading={isLoading}
                    handlePreviewButtonClick={handlePreviewButtonClick}
                    publications={
                        publicationsClone != undefined && publicationsClone.length > 0 ? publicationsClone : []
                    }
                    sortOrder={parsedSortOrder}
                    sortDirection={parsedSortDirection ? (parsedSortDirection as SORT_DIRECTION) : null}
                    onSort={handleSortChange}
                    handleFavoriteClick={handleFavoriteClick}
                    handleRemoveRecentClick={
                        stisPublicationFilter === 'recently-viewed' ? handleRemoveRecentClick : undefined
                    }
                    showLastViewed={stisPublicationFilter === 'recently-viewed'}
                    getPublicationVersions={getPublicationVersions}
                    isManageAdminRoute={isManageAdminRoute}
                    onPopupBlocked={handlePopupBlocked}
                    totalElements={pageDetail.totalElements}
                    emptyStateDescription={getPublicationsEmptyState(
                        {
                            filter: stisPublicationFilter,
                            hasSearchOrFilters: newTextSearch.length > 0 || Object.keys(additionalFilters).length > 0,
                            inVinSession: Boolean(selectedVin),
                        },
                        t
                    )}
                />
            </div>
            <DocsPagination
                setParams={setParams}
                hidePagination={stisPublicationFilter === 'recently-viewed'}
                pageDetail={
                    publicationsClone && publicationsClone.length > 0
                        ? pageDetail
                        : { size: 0, number: 0, totalElements: 0, totalPages: 0 }
                }
                feedbackContext={{
                    query: textSearchQuery,
                    searchType:
                        stisPublicationFilter === 'favorites'
                            ? 'favorite'
                            : stisPublicationFilter === 'recently-viewed'
                              ? 'recent'
                              : stisPublicationFilter,
                    carlineId: selectedVin
                        ? selectedVin.carlineId
                        : additionalFilters && additionalFilters.carline && additionalFilters.carline.length > 0
                          ? additionalFilters.carline[0].id
                          : '',
                    modelYear: selectedVin
                        ? selectedVin.vinRecord.modelYear
                        : additionalFilters && additionalFilters.modelYear
                          ? additionalFilters.modelYear
                          : '',
                    publicationTypeIds:
                        additionalFilters &&
                        additionalFilters.publicationTypes &&
                        additionalFilters.publicationTypes.length > 0
                            ? additionalFilters.publicationTypes.map(
                                  (selectedPublication: { id: string; publicationType: string }) =>
                                      selectedPublication.id
                              )
                            : [],
                    modelCodeId:
                        additionalFilters && additionalFilters.modelCode && additionalFilters.modelCode.length > 0
                            ? (
                                  vehicleModel.filter(
                                      (model: any) => model.code === additionalFilters.modelCode[0]
                                  )[0] as { id: string; code: string }
                              )?.id
                            : '',
                }}
                searchType={
                    stisPublicationFilter === 'favorites'
                        ? 'favorite'
                        : stisPublicationFilter === 'recently-viewed'
                          ? 'recent'
                          : stisPublicationFilter
                }
            />
        </div>
    );
};
